import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

/**
 * Raw data preview for an uploaded flat file — headers + the first N extracted rows,
 * so an operator can see the real data before mapping it. Backed by
 * GET /api/sessions/:id/uploads/:uploadId/preview (the same @ai/extractors path
 * the import uses, so the preview matches what gets imported).
 */
export function UploadPreview({ sessionId, uploadId, limit = 50 }: { sessionId: string; uploadId: string; limit?: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['upload-preview', sessionId, uploadId, limit],
    queryFn: () => api.getUploadPreview(sessionId, uploadId, { limit }),
  });

  if (isLoading) return <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>Loading preview…</div>;
  if (error) return <div className="text-[11px]" style={{ color: 'var(--accent-red)' }}>Error: {String(error)}</div>;

  const headers = data?.headers ?? [];
  const rows = data?.rows ?? [];
  if (rows.length === 0) return <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>No rows to preview.</div>;

  return (
    <div className="space-y-1">
      {data?.extractError && (
        <div
          className="text-[11px] rounded px-2 py-1"
          style={{ background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.4)', color: 'var(--text-secondary)' }}
        >
          ⚠ Extraction stopped early — showing the {rows.length} row(s) parsed before the error: {data.extractError}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--border)' }}>
        <table className="text-[11px]" style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-2 py-1 text-left font-mono whitespace-nowrap"
                  style={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                {headers.map((h) => {
                  const v = row[h] ?? '';
                  return (
                    <td
                      key={h}
                      className="px-2 py-1 font-mono whitespace-nowrap"
                      style={{ color: v === '' ? 'var(--text-dimmed)' : 'var(--text-primary)' }}
                    >
                      {v === '' ? '∅' : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
        {data?.truncated ? `Showing first ${rows.length} of ~${data.totalRowEstimate} rows` : `${rows.length} rows`}
      </div>
    </div>
  );
}
